/**
 * sqlite3mt — VFS registration glue for sqlite3-wasm
 *
 * This module bridges between the pure-logic VFS functions in vfs.ts
 * and sqlite3-wasm's struct-based VFS callback system (StructBinder +
 * installVfs). It is the only module that directly touches the
 * sqlite3-wasm WASM heap, pointer management, and struct binding API.
 *
 * §96 §2.4: Each Client Worker registers its own VFS instance pointing
 * to the shared SABs. SQLite calls our xRead/xWrite/xShmMap/etc.
 * callbacks; these delegate to the pure functions in vfs.ts.
 *
 * Architecture:
 *   registerClientWorkerVfs(sqlite3, conn, sab1, sab2, sab3, sab4, hooks)
 *     → creates sqlite3_io_methods struct (iVersion=2 for SHM)
 *     → creates sqlite3_vfs struct with xOpen/xDelete/xAccess
 *     → installs via sqlite3.vfs.installVfs()
 *     → returns a handle for lifetime management
 *
 * File routing (§96 §10 + §11):
 *   SQLITE_OPEN_MAIN_DB → SAB1 (slot window), xRead delegates to xReadMainDb()
 *   SQLITE_OPEN_WAL     → SAB2 (WAL bytes), xWrite delegates to xWriteWal()
 *   SQLITE_OPEN_MAIN_JOURNAL → no-op (WAL mode)
 *   SHM                 → SAB3 (wal-index + lock region), xShmMap/xShmLock
 *
 * SHM shadow management (§96 §17):
 *   xShmMap allocates WASM heap for each SHM region.
 *   xShmBarrier applies the masked push/full pull protocol over
 *   the WASM shadows backed by SAB3.
 *   xShmUnmap deallocates WASM shadows.
 *
 * Dependencies:
 *   - vfs.ts for ClientWorkerVfs interface and pure VFS functions
 *   - types.ts for ClientWorkerConnState
 *   - sab-layout.ts for SAB byte layout constants
 *   - sqlite3-wasm API (capi, wasm, vfs.installVfs)
 */

import type { ClientWorkerConnState } from './types'
import type { FaultRequestMessage } from './worker-bridge'
import {
    acquireWalWriteLock,
    prepareCommitWriteBeforeFirstWalByte,
    SQLITE_FULL,
    SQLITE_NOTFOUND,
    SQLITE_OPEN_CREATE,
    SQLITE_OPEN_MAIN_DB,
    SQLITE_OPEN_WAL,
    SQLITE_OPEN_MAIN_JOURNAL,
    SQLITE_SHM_LOCK,
    SQLITE_SHM_UNLOCK,
    SQLITE_SHM_SHARED,
    SQLITE_SHM_EXCLUSIVE,
    allowedBarrierMask,
    releaseWalWriteAfterCommit,
    xReadMainDb,
    xWriteMainDb,
    xWriteWal,
} from './vfs'
import {
    releaseWalWriteIfHeld,
    SQLITE_BUSY,
    SQLITE_IOERR,
    SQLITE_OK,
} from './client-commit-sync'
import {
    SAB1_DB_PAGE_COUNT_I32,
    SAB2_DATA_OFF,
    SAB3_SHM_DATA_SIZE,
    SHM_LOCK_RECOVER,
    SHM_LOCK_WRITE,
    Sab4Scalar,
    ensureSab2WalCapacity,
    sab2MaxWalByteLength,
    sab1CtrlView,
    sab4ScalarView,
    sab2SizeView,
    sab3LockView,
} from './sab-layout'
import {
    WAL_INDEX_HDR_B_OFF,
    WAL_INDEX_HDR_WORDS,
    sab3ShmInt32View,
} from './shm-ops'
import { sdkConsole } from '../logger'
import { WAL_HEADER_SIZE, WAL_FRAME_HEADER_SIZE, type PageHash, type WalCommitSyncMessage } from './types'

// ── sqlite3-wasm API surface ──────────────────────────────────

/**
 * The subset of the sqlite3-wasm API that VFS registration needs.
 *
 * This interface is satisfied by the global `sqlite3` object from
 * @sqlite.org/sqlite-wasm. It is injected at registration time so
 * that the VFS module remains testable without loading the actual
 * WASM binary.
 */
export interface Sqlite3WasmApi {
    capi: {
        sqlite3_io_methods: new () => Sqlite3IoMethodsStruct
        sqlite3_vfs: new (ptr?: number) => Sqlite3VfsStruct
        sqlite3_file: Sqlite3FileStructCtor
        sqlite3_vfs_find(name: number | null): number
    }
    wasm: {
        heap8u(): Uint8Array
        peek(ptr: number, type: string): unknown
        peek32(ptr: number): number
        poke(ptr: number, value: unknown, type: string): void
        poke32(ptr: number, value: number): void
        alloc(size: number): number
        dealloc(ptr: number): void
    }
    vfs: {
        installVfs(config: {
            io?: { struct: Sqlite3IoMethodsStruct; methods: Record<string, Function> }
            vfs?: { struct: Sqlite3VfsStruct; methods: Record<string, Function>; name: string }
        }): void
    }
}

/**
 * Minimal struct interface for sqlite3_io_methods (WASM side).
 * The actual struct is created by StructBinder at runtime.
 */
export interface Sqlite3IoMethodsStruct {
    pointer: number
    $iVersion: number
    dispose(): void
}

/**
 * Minimal struct interface for sqlite3_vfs (WASM side).
 */
export interface Sqlite3VfsStruct {
    pointer: number
    $iVersion: number
    $szOsFile: number
    $mxPathname: number
    $xRandomness: number
    $xSleep: number
    dispose(): void
}

export interface Sqlite3FileStruct {
    $pMethods: number
    dispose(): void
}

export interface Sqlite3FileStructCtor {
    new (ptr: number): Sqlite3FileStruct
    structInfo: { sizeof: number }
}

// ── File state ────────────────────────────────────────────────

/**
 * Per-open-file state tracked in JS, with auxiliary WASM allocations for SHM shadows.
 *
 * SQLite gives us one opaque pointer per open file (pFile).
 * We key our JS-side map by that pointer and separately manage any WASM
 * allocations we own, rather than mutating sqlite3_file storage.
 */
/** Tracked SHM region state for xShmBarrier dirty-word detection. */
export interface ShmRegionState {
    /** WASM heap pointer for the shadow copy. */
    wasmPtr: number
    /** Region size in bytes (must be 4-byte aligned). */
    size: number
    /** Clean baseline Int32Array for dirty-word comparison during xShmBarrier. */
    cleanI32: Int32Array
}

function holdsWalIndexHeaderWriteOwnership(myLocks: Int8Array): boolean {
    return myLocks[SHM_LOCK_WRITE] >= 0 || myLocks[SHM_LOCK_RECOVER] >= 0
}

function mirrorDirtyWalIndexHeaderIntoPrimaryCopy(
    sab3I32: Int32Array,
    activeI32: Int32Array,
    cleanI32: Int32Array
): void {
    const headerBWordBase = WAL_INDEX_HDR_B_OFF / Int32Array.BYTES_PER_ELEMENT

    for (let wordIndex = 0; wordIndex < WAL_INDEX_HDR_WORDS; wordIndex += 1) {
        const bWordIndex = headerBWordBase + wordIndex
        if (activeI32[bWordIndex] !== cleanI32[bWordIndex]) {
            Atomics.store(sab3I32, wordIndex, activeI32[bWordIndex]!)
        }
    }
}

export interface RegisteredFileState {
    /** File type routing */
    fileType: VfsFileKind
    /** Connection this file belongs to */
    conn: ClientWorkerConnState
    /** SHM shadow regions: iRegion → ShmRegionState with clean baseline */
    shmRegions: Map<number, ShmRegionState>
    /** Current lock level (0=none, 1=shared, 2=reserved, 3=pending, 4=exclusive) */
    lockLevel: number
    /** WASM heap pointer for this struct (for deallocation) */
    selfPtr: number
    /** Whether SQLite has already created/opened this logical file */
    exists: boolean
}

/**
 * File kind for VFS routing.
 */
export const enum VfsFileKind {
    MainDb = 1,
    Wal = 2,
    Journal = 3,
    Shm = 4,
    Other = 0,
}

const SQLITE_CANTOPEN = 14
const SQLITE_LOCK_NONE = 0
const SQLITE_LOCK_SHARED = 1
const SQLITE_LOCK_RESERVED = 2
const SQLITE_LOCK_PENDING = 3
const SQLITE_LOCK_EXCLUSIVE = 4

function cStringFromHeap(heap: Uint8Array, ptr: number): string {
    if (!ptr) {
        return ''
    }

    let end = ptr
    while (end < heap.length && heap[end] !== 0) {
        end += 1
    }
    return new TextDecoder().decode(heap.subarray(ptr, end))
}

function classifyVirtualFilename(name: string): VfsFileKind {
    const bareName = name.split('?', 1)[0] ?? name
    if (bareName.endsWith('-wal')) {
        return VfsFileKind.Wal
    }
    if (bareName.endsWith('-journal')) {
        return VfsFileKind.Journal
    }
    if (bareName.endsWith('-shm')) {
        return VfsFileKind.Shm
    }
    return VfsFileKind.MainDb
}

// ── Internal helpers ────────────────────────────────────────────

/** Read big-endian uint32 from a Uint8Array. */
function readU32BE(bytes: Uint8Array, offset: number): number {
    return (
        ((bytes[offset]! << 24) >>> 0)
        | (bytes[offset + 1]! << 16)
        | (bytes[offset + 2]! << 8)
        | bytes[offset + 3]!
    ) >>> 0
}

function detectCommitFrameNoFromWalWrite(
    sab2: SharedArrayBuffer,
    walOffset: number,
    byteLength: number,
    pageSize: number,
): number | null {
    if (!Number.isInteger(walOffset) || !Number.isInteger(byteLength) || byteLength <= 0) {
        return null
    }

    const writeStart = walOffset
    const writeEnd = walOffset + byteLength
    if (writeEnd <= WAL_HEADER_SIZE) {
        return null
    }

    const frameSize = WAL_FRAME_HEADER_SIZE + pageSize
    const firstRelevantByte = Math.max(writeStart, WAL_HEADER_SIZE)
    const firstFrameIndex = Math.floor((firstRelevantByte - WAL_HEADER_SIZE) / frameSize)
    const lastFrameIndex = Math.floor((writeEnd - 1 - WAL_HEADER_SIZE) / frameSize)
    const walBytes = new Uint8Array(sab2, SAB2_DATA_OFF, sab2.byteLength - SAB2_DATA_OFF)

    let detectedCommitFrameNo: number | null = null

    for (let frameIndex = firstFrameIndex; frameIndex <= lastFrameIndex; frameIndex += 1) {
        const frameHeaderOffset = WAL_HEADER_SIZE + frameIndex * frameSize
        const commitFieldOffset = frameHeaderOffset + 4
        const commitFieldEnd = commitFieldOffset + 4

        if (commitFieldEnd <= writeStart || commitFieldOffset >= writeEnd) {
            continue
        }
        if (commitFieldEnd > walBytes.length) {
            continue
        }

        const nPage = readU32BE(walBytes, commitFieldOffset)
        if (nPage !== 0) {
            detectedCommitFrameNo = frameIndex + 1
        }
    }

    return detectedCommitFrameNo
}

/**
 * Atomically acquire a shared SHM lock.
 * Returns true on success, false if an exclusive holder prevents it.
 * Uses a CAS loop to avoid TOCTOU between the check and the increment.
 */
function lockSharedAtomic(lockView: Int32Array, index: number): boolean {
    for (;;) {
        const cur = Atomics.load(lockView, index)
        if (cur < 0) return false
        if (Atomics.compareExchange(lockView, index, cur, cur + 1) === cur) {
            return true
        }
    }
}

// ── Hooks (dependency injection for VFS callbacks) ────────────

/**
 * Hooks that the VFS registration calls back into for operations
 * that require Worker messaging or async coordination.
 *
 * These are injected at registration time, decoupling the WASM
 * glue from the actual Worker postMessage / Atomics.wait loop.
 */
export interface VfsRegistrationHooks {
    /** Post a fault request to the Control Worker */
    postFaultMessage(conn: ClientWorkerConnState, msg: FaultRequestMessage): void

    /**
     * Hash a committed page image before posting wal-commit-sync.
     *
     * The surrounding runtime injects the canonical page-hash function so the
     * VFS glue does not need to know about manifest/durable-store internals.
     */
    hashPage(pageData: Uint8Array): PageHash

    /**
     * Deliver wal-commit-sync to the Control Worker bridge.
     *
     * In production this should hand the message off to another worker/runtime
     * that can publish COMMIT_ACK independently of the caller's thread. Tests
     * may ACK synchronously inside this hook.
     */
    postCommitSyncMessage(
        conn: ClientWorkerConnState,
        msg: WalCommitSyncMessage,
        transfer: ArrayBuffer[],
    ): void

    /**
     * Enter fatal I/O mode for this connection/runtime generation.
     */
    enterFatalIoMode(conn: ClientWorkerConnState): void
}

// ── Registration result ───────────────────────────────────────

export interface ReleaseLeakedShmLocksOptions {
    /**
     * Finalize normally-leaked pending commits by default. Failed nonfatal
     * SQLite requests may instead abandon the uncertain pending-commit marker so
     * cleanup cannot turn a transient SQLITE_BUSY into runtime-fatal state.
     */
    pendingCommit?: 'finalize' | 'abandon'
}

/**
 * Handle returned by registerClientWorkerVfs().
 * Call dispose() to unregister and clean up WASM resources.
 */
export interface VfsRegistrationHandle {
    /** The registered VFS name (unique per Client Worker) */
    readonly vfsName: string
    /** The sqlite3_io_methods struct pointer */
    readonly ioPointer: number
    /** Push the current WASM SHM shadows into the shared SAB3 backing store. */
    pushMappedShmRegionsToShared(): void
    /** Pull the shared SHM bytes into all mapped WASM shadows for this VFS. */
    pullMappedShmRegionsFromShared(): void
    /**
     * Drop any SHM locks still attributed to this VFS' live connections.
     *
     * This is intended for post-request and shutdown cleanup when SQLite has
     * already finished a statement/transaction but a connection-local lock was
     * left behind by an unexpected path.
     */
    releaseLeakedShmLocks(options?: ReleaseLeakedShmLocksOptions): void
    /** Dispose the VFS registration and free WASM allocations */
    dispose(): void
}

// ── Main registration function ────────────────────────────────

/**
 * Register a custom VFS for a Client Worker's sqlite3-wasm instance.
 *
 * This creates the WASM structs (sqlite3_io_methods, sqlite3_vfs),
 * wires their callbacks to the pure VFS functions from vfs.ts, and
 * installs them via sqlite3.vfs.installVfs().
 *
 * After registration, the Client Worker can open databases using
 * this VFS: `sqlite3.open_v2(filename, { vfs: vfsName })`.
 *
 * @param sqlite3 - The sqlite3-wasm API object
 * @param conn - Per-connection state (connId, runtimeEpoch, etc.)
 * @param sab1 - MainDb slot window
 * @param sab2 - WAL bytes
 * @param sab3 - SHM / wal-index + lock region
 * @param sab4 - Control scalars (gates, frontier, commit ACKs)
 * @param hooks - Callbacks for fault posting
 * @param name - VFS name (defaults to 'secureblob-mt-{connId}')
 * @returns Handle for lifetime management
 */
export function registerClientWorkerVfs(
    sqlite3: Sqlite3WasmApi,
    conn: ClientWorkerConnState,
    pageSize: number,
    sab1: SharedArrayBuffer,
    sab2: SharedArrayBuffer,
    sab3: SharedArrayBuffer,
    sab4: SharedArrayBuffer,
    hooks: VfsRegistrationHooks,
    name?: string,
): VfsRegistrationHandle {
    const vfsName = name ?? `secureblob-mt-${conn.connId}`
    const { capi, wasm } = sqlite3

    // Active file states: pFile pointer → RegisteredFileState
    const openFiles = new Map<number, RegisteredFileState>()
    let journalBuffer = new Uint8Array(0)
    let journalSize = 0
    let mainDbCreated = Atomics.load(sab1CtrlView(sab1), SAB1_DB_PAGE_COUNT_I32) > 0
    let walCreated = false
    let journalCreated = false
    let mainDbSharedLocksHeld = 0
    let seenWalIndexResetSeq = Atomics.load(
        sab4ScalarView(sab4),
        Sab4Scalar.WAL_INDEX_RESET_SEQ,
    )

    const walSizeView = sab2SizeView(sab2)
    const sab4Scalar = sab4ScalarView(sab4)

    const hasMainDb = () => mainDbCreated || Atomics.load(sab1CtrlView(sab1), SAB1_DB_PAGE_COUNT_I32) > 0
    const hasWal = () => walCreated || Atomics.load(walSizeView, 0) > 0
    const hasJournal = () => journalCreated || journalSize > 0
    const writerOwnerToken = conn.connId + 1

    function logLockTrace(event: string, payload: Record<string, unknown>): void {
        sdkConsole.trace(`[sqlite3mt:vfs] ${event} ` + JSON.stringify({
            connId: conn.connId,
            ...payload,
        }))
    }

    function loadMainDbWriterOwner(): number {
        return Atomics.load(sab4Scalar, Sab4Scalar.MAIN_DB_WRITER_CONN)
    }

    function clearMainDbWriterIfOwned(): void {
        Atomics.compareExchange(sab4Scalar, Sab4Scalar.MAIN_DB_WRITER_CONN, writerOwnerToken, 0)
    }

    function releaseMainDbSharedLock(): void {
        if (mainDbSharedLocksHeld <= 0) {
            return
        }

        mainDbSharedLocksHeld -= 1
        Atomics.sub(sab4Scalar, Sab4Scalar.MAIN_DB_READER_COUNT, 1)
    }

    function acquireMainDbSharedLock(file: RegisteredFileState): number {
        while (true) {
            const ownerBefore = loadMainDbWriterOwner()
            if (ownerBefore !== 0 && ownerBefore !== writerOwnerToken) {
                return SQLITE_BUSY
            }

            const readerCount = Atomics.load(sab4Scalar, Sab4Scalar.MAIN_DB_READER_COUNT)
            if (Atomics.compareExchange(
                sab4Scalar,
                Sab4Scalar.MAIN_DB_READER_COUNT,
                readerCount,
                readerCount + 1,
            ) !== readerCount) {
                continue
            }

            const ownerAfter = loadMainDbWriterOwner()
            if (ownerAfter === 0 || ownerAfter === writerOwnerToken) {
                mainDbSharedLocksHeld += 1
                file.lockLevel = SQLITE_LOCK_SHARED
                return SQLITE_OK
            }

            Atomics.sub(sab4Scalar, Sab4Scalar.MAIN_DB_READER_COUNT, 1)
            return SQLITE_BUSY
        }
    }

    function acquireMainDbWriterOwnership(): number {
        while (true) {
            const owner = loadMainDbWriterOwner()
            if (owner === writerOwnerToken) {
                return SQLITE_OK
            }
            if (owner !== 0) {
                return SQLITE_BUSY
            }
            if (Atomics.compareExchange(
                sab4Scalar,
                Sab4Scalar.MAIN_DB_WRITER_CONN,
                0,
                writerOwnerToken,
            ) === 0) {
                return SQLITE_OK
            }
        }
    }

    function lockMainDb(file: RegisteredFileState, lockLevel: number): number {
        if (lockLevel <= file.lockLevel) {
            return SQLITE_OK
        }

        switch (lockLevel) {
            case SQLITE_LOCK_SHARED:
                return acquireMainDbSharedLock(file)

            case SQLITE_LOCK_RESERVED:
            case SQLITE_LOCK_PENDING: {
                const rc = acquireMainDbWriterOwnership()
                if (rc === SQLITE_OK) {
                    file.lockLevel = lockLevel
                }
                return rc
            }

            case SQLITE_LOCK_EXCLUSIVE: {
                const rc = acquireMainDbWriterOwnership()
                if (rc !== SQLITE_OK) {
                    return rc
                }

                const readerCount = Atomics.load(sab4Scalar, Sab4Scalar.MAIN_DB_READER_COUNT)
                const foreignReaderCount = Math.max(0, readerCount - mainDbSharedLocksHeld)
                if (foreignReaderCount > 0) {
                    // Failing EXCLUSIVE escalation must not retain writer ownership.
                    // Keeping MAIN_DB_WRITER_CONN pinned here leaves the connection in a
                    // self-owned write-locked limbo even though SQLite saw SQLITE_BUSY.
                    clearMainDbWriterIfOwned()
                    return SQLITE_BUSY
                }

                file.lockLevel = SQLITE_LOCK_EXCLUSIVE
                return SQLITE_OK
            }

            default:
                file.lockLevel = lockLevel
                return SQLITE_OK
        }
    }

    function unlockMainDb(file: RegisteredFileState, lockLevel: number): number {
        if (lockLevel >= file.lockLevel) {
            return SQLITE_OK
        }

        if (lockLevel === SQLITE_LOCK_SHARED) {
            if (file.lockLevel > SQLITE_LOCK_SHARED) {
                clearMainDbWriterIfOwned()
            }
            file.lockLevel = SQLITE_LOCK_SHARED
            return SQLITE_OK
        }

        if (file.lockLevel > SQLITE_LOCK_SHARED) {
            clearMainDbWriterIfOwned()
        }
        if (file.lockLevel >= SQLITE_LOCK_SHARED) {
            releaseMainDbSharedLock()
        }
        file.lockLevel = SQLITE_LOCK_NONE
        return SQLITE_OK
    }

    function releaseLeakedMainDbLocksForConnection(connState: ClientWorkerConnState): void {
        for (const file of openFiles.values()) {
            if (file.conn !== connState || file.fileType !== VfsFileKind.MainDb) {
                continue
            }
            if (file.lockLevel !== SQLITE_LOCK_NONE) {
                unlockMainDb(file, SQLITE_LOCK_NONE)
            }
        }
    }

    function clearMappedShmRegions(): void {
        for (const file of openFiles.values()) {
            for (const region of file.shmRegions.values()) {
                const activeI32 = new Int32Array(wasm.heap8u().buffer, region.wasmPtr, region.size / Int32Array.BYTES_PER_ELEMENT)
                activeI32.fill(0)
                region.cleanI32.fill(0)
            }
        }
    }

    function resetWalIndexAfterZeroTruncate(): void {
        sab3ShmInt32View(sab3).fill(0)
        clearMappedShmRegions()
        const scalar = sab4ScalarView(sab4)
        Atomics.add(scalar, Sab4Scalar.WAL_INDEX_RESET_SEQ, 1)
        Atomics.notify(scalar, Sab4Scalar.WAL_INDEX_RESET_SEQ)
        seenWalIndexResetSeq = Atomics.load(scalar, Sab4Scalar.WAL_INDEX_RESET_SEQ)
    }

    function syncMappedShmRegionsAfterExternalReset(): void {
        const currentSeq = Atomics.load(sab4Scalar, Sab4Scalar.WAL_INDEX_RESET_SEQ)
        if (currentSeq === seenWalIndexResetSeq) {
            return
        }

        for (const file of openFiles.values()) {
            for (const [iRegion, region] of file.shmRegions) {
                const sharedByteOffset = iRegion * region.size
                const shmByteLen = Math.min(region.size, SAB3_SHM_DATA_SIZE - sharedByteOffset)
                const active = new Uint8Array(wasm.heap8u().buffer, region.wasmPtr, region.size)
                active.fill(0)
                if (shmByteLen > 0) {
                    active.set(new Uint8Array(sab3, sharedByteOffset, shmByteLen))
                }
                const activeI32 = new Int32Array(wasm.heap8u().buffer, region.wasmPtr, region.size / Int32Array.BYTES_PER_ELEMENT)
                region.cleanI32.set(activeI32)
            }
        }
        seenWalIndexResetSeq = currentSeq
    }

    function releaseRawWalWriteLock(connState: ClientWorkerConnState): void {
        Atomics.store(sab3LockView(sab3), 0, 0)
        connState.myLocks[0] = -1
    }

    function releaseLeakedShmLocksForConnection(
        connState: ClientWorkerConnState,
        options: ReleaseLeakedShmLocksOptions = {},
    ): void {
        const lockView = sab3LockView(sab3)
        const scalarView = sab4ScalarView(sab4)
        const hasRawWalWriteLock = connState.myLocks[0] === 1
        const hasPendingCommit = connState.pendingCommitFrame > 0
        const shouldFinalizePendingCommit = options.pendingCommit !== 'abandon'
        const salvageUntrackedWalWrite = hasPendingCommit && hasRawWalWriteLock && !connState.holdsWalWrite

        if (shouldFinalizePendingCommit && hasPendingCommit && (connState.holdsWalWrite || hasRawWalWriteLock)) {
            if (salvageUntrackedWalWrite && Atomics.load(scalarView, Sab4Scalar.WAL_WRITE_HOLDERS) > 0) {
                connState.holdsWalWrite = true
            }
            releaseWalWriteAfterCommit(connState, sab2, sab3, sab4, {
                hashPage: hooks.hashPage,
                postCommitSync(message, transfer) {
                    hooks.postCommitSyncMessage(connState, message, transfer)
                },
                enterFatalIoModeForConnection() {
                    hooks.enterFatalIoMode(connState)
                },
                releaseWalWriteLock() {
                    releaseRawWalWriteLock(connState)
                },
            })
            if (salvageUntrackedWalWrite) {
                connState.holdsWalWrite = false
                if (connState.myLocks[0] === 1) {
                    releaseRawWalWriteLock(connState)
                }
            }
        } else if (connState.holdsWalWrite) {
            releaseWalWriteIfHeld(connState, sab4, () => releaseRawWalWriteLock(connState))
        } else if (connState.myLocks[0] === 1) {
            releaseRawWalWriteLock(connState)
        } else if (connState.myLocks[0] === 0) {
            Atomics.sub(lockView, 0, 1)
            connState.myLocks[0] = -1
        }

        connState.pendingCommitFrame = 0
        connState.txStartFrame = 0

        for (let i = 1; i < connState.myLocks.length; i += 1) {
            if (connState.myLocks[i] === 1) {
                Atomics.store(lockView, i, 0)
                connState.myLocks[i] = -1
                continue
            }
            if (connState.myLocks[i] === 0) {
                Atomics.sub(lockView, i, 1)
                connState.myLocks[i] = -1
            }
        }

        releaseLeakedMainDbLocksForConnection(connState)
    }

    // ── io_methods (§96 §10.3 xRead, §10.5 xWrite, §17 xShmBarrier, etc.) ─

    const sbIo = new capi.sqlite3_io_methods()
    sbIo.$iVersion = 2 // iVersion=2 → SHM methods enabled

    const ioMethods = {
        /**
         * xRead — file-type-aware read routing.
         *
         * MainDb: delegate to xReadMainDb() from vfs.ts
         * WAL:    read from SAB2 WAL shadow
         * Other:  SQLITE_IOERR
         */
        xRead(pFile: number, pDest: number, n: number, offset64: bigint): number {
            const file = openFiles.get(pFile)
            sdkConsole.trace('[sqlite3mt:vfs] xRead:start ' + JSON.stringify({ pFile, n, offset: Number(offset64), fileType: file?.fileType ?? null }))
            if (!file) return SQLITE_IOERR

            if (file.fileType === VfsFileKind.MainDb) {
                const dst = new Uint8Array(wasm.heap8u().buffer, pDest, n)
                let remaining = n
                let byteOffset = Number(offset64)
                let dstOffset = 0

                while (remaining > 0) {
                    const pgno = Math.floor(byteOffset / pageSize) + 1
                    const pageOffset = byteOffset % pageSize
                    const chunkLength = Math.min(pageSize - pageOffset, remaining)
                    const rc = xReadMainDb(
                        file.conn,
                        sab1,
                        sab4,
                        pgno,
                        dst.subarray(dstOffset, dstOffset + chunkLength),
                        (msg: FaultRequestMessage) => hooks.postFaultMessage(file.conn, msg),
                        pageOffset,
                    )

                    if (rc === SQLITE_OK) {
                        byteOffset += chunkLength
                        dstOffset += chunkLength
                        remaining -= chunkLength
                        continue
                    }

                    if (rc === (SQLITE_IOERR | (2 << 8))) {
                        dst.fill(0, dstOffset + chunkLength)
                        return rc
                    }

                    return rc
                }

                mainDbCreated = true
                sdkConsole.trace('[sqlite3mt:vfs] xRead:main:ok ' + JSON.stringify({ offset: Number(offset64), n }))
                return SQLITE_OK
            }

            if (file.fileType === VfsFileKind.Wal) {
                // Read from SAB2 WAL shadow
                const walView = new Uint8Array(sab2, SAB2_DATA_OFF, sab2.byteLength - SAB2_DATA_OFF)
                const walOffset = Number(offset64)
                if (walOffset + n > walView.length) {
                    wasm.heap8u().fill(0, pDest, pDest + n)
                    return (SQLITE_IOERR | (2 << 8)) // SQLITE_IOERR_SHORT_READ
                }
                wasm.heap8u().set(walView.subarray(walOffset, walOffset + n), pDest)
                return SQLITE_OK
            }

            if (file.fileType === VfsFileKind.Journal) {
                const offset = Number(offset64)
                if (offset + n > journalSize) {
                    wasm.heap8u().fill(0, pDest, pDest + n)
                    return (SQLITE_IOERR | (2 << 8))
                }
                wasm.heap8u().set(journalBuffer.subarray(offset, offset + n), pDest)
                return SQLITE_OK
            }

            wasm.heap8u().fill(0, pDest, pDest + n)
            return (SQLITE_IOERR | (2 << 8))
        },

        /**
         * xWrite — file-type-aware write routing.
         *
         * MainDb: delegate to xWriteMainDb() (slot cache update)
         * WAL:    delegate to xWriteWal() (frame copy + commit detection)
         * Other:  SQLITE_IOERR
         */
        xWrite(pFile: number, pSrc: number, n: number, offset64: bigint): number {
            const file = openFiles.get(pFile)
            sdkConsole.trace('[sqlite3mt:vfs] xWrite:start ' + JSON.stringify({ pFile, n, offset: Number(offset64), fileType: file?.fileType ?? null }))
            if (!file) return SQLITE_IOERR

            if (file.fileType === VfsFileKind.MainDb) {
                const src = new Uint8Array(wasm.heap8u().buffer, pSrc, n)
                let remaining = src.byteLength
                let byteOffset = Number(offset64)
                let srcOffset = 0

                while (remaining > 0) {
                    const pgno = Math.floor(byteOffset / pageSize) + 1
                    const pageOffset = byteOffset % pageSize
                    const chunkLength = Math.min(pageSize - pageOffset, remaining)
                    const rc = xWriteMainDb(
                        file.conn,
                        sab1,
                        pgno,
                        src.subarray(srcOffset, srcOffset + chunkLength),
                        pageOffset,
                        pageSize
                    )
                    if (rc !== SQLITE_OK) {
                        return rc
                    }
                    byteOffset += chunkLength
                    srcOffset += chunkLength
                    remaining -= chunkLength
                }

                return SQLITE_OK
            }

            if (file.fileType === VfsFileKind.Wal) {
                if (file.conn.holdsWalWrite && file.conn.txStartFrame === 0 && n > 0) {
                    const prepareRc = prepareCommitWriteBeforeFirstWalByte(file.conn, sab3, sab4, {
                        enterFatalIoModeForConnection: () => { hooks.enterFatalIoMode(file.conn) },
                    })
                    if (prepareRc !== SQLITE_OK) {
                        // No WAL bytes have been written yet. Releasing the raw
                        // WAL_WRITE ownership here prevents a checkpoint-quiesce
                        // deadlock where the checkpoint gate is closed and the
                        // client keeps holding slot 0 while busy-retrying.
                        if (file.conn.holdsWalWrite) {
                            releaseWalWriteIfHeld(file.conn, sab4, () => releaseRawWalWriteLock(file.conn))
                        } else if (file.conn.myLocks[0] === 1) {
                            releaseRawWalWriteLock(file.conn)
                        }
                        return prepareRc
                    }
                }

                // §11.1.3: Copy WAL bytes into SAB2; detect commit frames
                const walOffset = Number(offset64)
                const src = new Uint8Array(wasm.heap8u().buffer, pSrc, n)

                walCreated = true
                const rc = xWriteWal(file.conn, sab2, walOffset, src, false, null)
                if (rc !== SQLITE_OK) {
                    return rc
                }

                const commitFrameNo = detectCommitFrameNoFromWalWrite(
                    sab2,
                    walOffset,
                    src.byteLength,
                    pageSize,
                )
                if (commitFrameNo != null) {
                    file.conn.pendingCommitFrame = commitFrameNo
                }

                return SQLITE_OK
            }

            if (file.fileType === VfsFileKind.Journal) {
                const offset = Number(offset64)
                const needed = offset + n
                if (needed > journalBuffer.length) {
                    const nextBuffer = new Uint8Array(Math.max(needed * 2, 64 * 1024))
                    nextBuffer.set(journalBuffer)
                    journalBuffer = nextBuffer
                }
                journalBuffer.set(wasm.heap8u().subarray(pSrc, pSrc + n), offset)
                if (needed > journalSize) {
                    journalSize = needed
                }
                journalCreated = true
                return SQLITE_OK
            }

            return SQLITE_OK
        },

        /**
         * xSync — no-op in WAL mode (commit detection in xWrite).
         */
        xSync(pFile: number, _flags: number): number {
            return 0 // SQLITE_OK
        },

        /**
         * xClose — release file state and WASM allocations.
         */
        xClose(pFile: number): number {
            const file = openFiles.get(pFile)
            if (file) {
                if (file.fileType === VfsFileKind.MainDb && file.lockLevel !== SQLITE_LOCK_NONE) {
                    unlockMainDb(file, SQLITE_LOCK_NONE)
                }
                // Deallocate SHM regions
                for (const [_, region] of file.shmRegions) {
                    wasm.dealloc(region.wasmPtr)
                }
                file.shmRegions.clear()
                // Deallocate file state struct
                if (file.selfPtr) wasm.dealloc(file.selfPtr)
                openFiles.delete(pFile)
            }
            return SQLITE_OK
        },

        /**
         * xFileSize — report file size based on type.
         */
        xFileSize(pFile: number, pSz64: number): number {
            const file = openFiles.get(pFile)
            sdkConsole.trace('[sqlite3mt:vfs] xFileSize:start ' + JSON.stringify({ pFile, fileType: file?.fileType ?? null }))
            if (!file) {
                wasm.poke(pSz64, 0n, 'i64')
                return 0
            }

            if (file.fileType === VfsFileKind.MainDb) {
                const dbPageCount = Atomics.load(sab1CtrlView(sab1), SAB1_DB_PAGE_COUNT_I32)
                wasm.poke(pSz64, BigInt(dbPageCount) * BigInt(pageSize), 'i64')
                sdkConsole.trace('[sqlite3mt:vfs] xFileSize:main ' + JSON.stringify({ dbPageCount, pageSize, bytes: dbPageCount * pageSize }))
            } else if (file.fileType === VfsFileKind.Wal) {
                const walSize = new Int32Array(sab2, 0, 1)[0]
                wasm.poke(pSz64, BigInt(walSize), 'i64')
            } else if (file.fileType === VfsFileKind.Journal) {
                wasm.poke(pSz64, BigInt(journalSize), 'i64')
            } else {
                wasm.poke(pSz64, 0n, 'i64')
            }
            return 0
        },

        /** xLock — in-memory lock level tracking */
        xLock(pFile: number, lockLevel: number): number {
            const file = openFiles.get(pFile)
            logLockTrace('xLock:start', { pFile, lockLevel, fileType: file?.fileType ?? null })
            if (!file) return SQLITE_IOERR
            if (file.fileType === VfsFileKind.MainDb) {
                const rc = lockMainDb(file, lockLevel)
                logLockTrace('xLock:done', {
                    pFile,
                    lockLevel,
                    rc,
                    fileType: file.fileType,
                    fileLockLevel: file.lockLevel,
                    mainDbReaderCount: Atomics.load(sab4Scalar, Sab4Scalar.MAIN_DB_READER_COUNT),
                    mainDbWriterConn: Atomics.load(sab4Scalar, Sab4Scalar.MAIN_DB_WRITER_CONN),
                })
                return rc
            }
            file.lockLevel = lockLevel
            logLockTrace('xLock:done', { pFile, lockLevel, rc: SQLITE_OK, fileType: file.fileType, fileLockLevel: file.lockLevel })
            return SQLITE_OK
        },

        /** xUnlock — in-memory lock level tracking */
        xUnlock(pFile: number, lockLevel: number): number {
            const file = openFiles.get(pFile)
            logLockTrace('xUnlock:start', { pFile, lockLevel, fileType: file?.fileType ?? null })
            if (!file) return SQLITE_IOERR
            if (file.fileType === VfsFileKind.MainDb) {
                const rc = unlockMainDb(file, lockLevel)
                logLockTrace('xUnlock:done', {
                    pFile,
                    lockLevel,
                    rc,
                    fileType: file.fileType,
                    fileLockLevel: file.lockLevel,
                    mainDbReaderCount: Atomics.load(sab4Scalar, Sab4Scalar.MAIN_DB_READER_COUNT),
                    mainDbWriterConn: Atomics.load(sab4Scalar, Sab4Scalar.MAIN_DB_WRITER_CONN),
                })
                return rc
            }
            file.lockLevel = lockLevel
            logLockTrace('xUnlock:done', { pFile, lockLevel, rc: SQLITE_OK, fileType: file.fileType, fileLockLevel: file.lockLevel })
            return SQLITE_OK
        },

        /** xCheckReservedLock — reflect cross-connection main-db writer ownership */
        xCheckReservedLock(pFile: number, pOut: number): number {
            const file = openFiles.get(pFile)
            const reserved = file?.fileType === VfsFileKind.MainDb && loadMainDbWriterOwner() !== 0 ? 1 : 0
            wasm.poke32(pOut, reserved)
            logLockTrace('xCheckReservedLock', {
                pFile,
                fileType: file?.fileType ?? null,
                reserved,
                mainDbReaderCount: Atomics.load(sab4Scalar, Sab4Scalar.MAIN_DB_READER_COUNT),
                mainDbWriterConn: Atomics.load(sab4Scalar, Sab4Scalar.MAIN_DB_WRITER_CONN),
            })
            return SQLITE_OK
        },

        /** xTruncate — WAL truncation support */
        xTruncate(pFile: number, sz64: bigint): number {
            const file = openFiles.get(pFile)
            if (file?.fileType === VfsFileKind.Journal) {
                journalSize = Math.max(0, Number(sz64))
            } else if (file?.fileType === VfsFileKind.Wal) {
                const requestedSize = Number(sz64)
                if (!Number.isFinite(requestedSize) || requestedSize < 0) {
                    return SQLITE_IOERR
                }
                if (!ensureSab2WalCapacity(sab2, requestedSize)) {
                    sdkConsole.error('[sqlite3mt:vfs] WAL truncate exceeded shadow capacity', {
                        connId: conn.connId,
                        requestedWalBytes: requestedSize,
                        currentWalBytes: Atomics.load(walSizeView, 0),
                        currentSab2Bytes: sab2.byteLength,
                        maxWalBytes: sab2MaxWalByteLength(sab2),
                        walWriteHolders: Atomics.load(sab4Scalar, Sab4Scalar.WAL_WRITE_HOLDERS),
                        mainDbReaderCount: Atomics.load(sab4Scalar, Sab4Scalar.MAIN_DB_READER_COUNT),
                        mainDbWriterConn: Atomics.load(sab4Scalar, Sab4Scalar.MAIN_DB_WRITER_CONN),
                    })
                    return SQLITE_FULL
                }
                const nextSize = requestedSize
                const previousSize = Atomics.load(walSizeView, 0)
                if (nextSize < previousSize) {
                    new Uint8Array(sab2, SAB2_DATA_OFF + nextSize, previousSize - nextSize).fill(0)
                }
                Atomics.store(walSizeView, 0, nextSize)
                walCreated = nextSize > 0
                if (nextSize === 0) {
                    resetWalIndexAfterZeroTruncate()
                }
            }
            return 0
        },

        xSectorSize(_pFile: number): number {
            sdkConsole.trace('[sqlite3mt:vfs] xSectorSize ' + JSON.stringify({
                fileType: openFiles.get(_pFile)?.fileType,
            }))
            // Report conservative defaults. This VFS is a synthetic SAB-backed
            // transport and must not advertise stronger sector guarantees than it
            // actually provides.
            return 0
        },
        xDeviceCharacteristics(_pFile: number): number {
            sdkConsole.trace('[sqlite3mt:vfs] xDeviceCharacteristics ' + JSON.stringify({
                fileType: openFiles.get(_pFile)?.fileType,
            }))
            return 0
        },
        xFileControl(_pFile: number, opId: number, _pArg: number): number {
            sdkConsole.trace('[sqlite3mt:vfs] xFileControl:notfound', { opId })
            return SQLITE_NOTFOUND
        },

        // ── SHM methods (iVersion=2) ──────────────────────────

        /**
         * xShmMap — allocate WASM shadow for SHM region.
         *
         * §96 §4.3: sqlite3 expects an SQLite rc and an out-pointer. The VFS
         * must write the mapped region pointer via pOut and return SQLITE_OK,
         * not return the pointer as the rc.
         */
        xShmMap(pFile: number, iRegion: number, szRegion: number, isWrite: number, pOut: number): number {
            const file = openFiles.get(pFile)
            sdkConsole.trace('[sqlite3mt:vfs] xShmMap ' + JSON.stringify({ pFile, iRegion, szRegion, isWrite, pOut, fileType: file?.fileType ?? null }))
            if (!file) return SQLITE_IOERR
            syncMappedShmRegionsAfterExternalReset()

            let region = file.shmRegions.get(iRegion)
            if (region === undefined) {
                const sharedByteOffset = iRegion * szRegion
                if (sharedByteOffset < 0 || sharedByteOffset >= SAB3_SHM_DATA_SIZE) {
                    return SQLITE_IOERR
                }

                const wasmPtr = wasm.alloc(szRegion)
                // Initialize WASM shadow from SAB3 SHM data
                const shmByteLen = Math.min(szRegion, SAB3_SHM_DATA_SIZE - sharedByteOffset)
                if (shmByteLen > 0) {
                    const shmData = new Uint8Array(sab3, sharedByteOffset, shmByteLen)
                    wasm.heap8u().set(shmData, wasmPtr)
                }
                // Create clean baseline for xShmBarrier dirty detection
                const wordCount = szRegion / Int32Array.BYTES_PER_ELEMENT
                const cleanI32 = new Int32Array(wordCount)
                const activeI32 = new Int32Array(wasm.heap8u().buffer, wasmPtr, wordCount)
                cleanI32.set(activeI32)

                region = { wasmPtr, size: szRegion, cleanI32 }
                file.shmRegions.set(iRegion, region)
            }

            wasm.poke32(pOut, region.wasmPtr)
            return SQLITE_OK
        },

        /**
         * xShmLock — delegate to SAB3 lock region Atomics.
         *
         * §96 §4.3: WAL locks via Atomics on the lock region at end of SAB3.
         */
        xShmLock(pFile: number, offset: number, n: number, flags: number): number {
            const file = openFiles.get(pFile)
            syncMappedShmRegionsAfterExternalReset()
            const lockView = sab3LockView(sab3)
            logLockTrace('xShmLock:start', {
                pFile,
                offset,
                n,
                flags,
                fileType: file?.fileType ?? null,
                lockView: Array.from(lockView),
                myLocks: Array.from(file?.conn.myLocks ?? []),
            })
            if (!file) return SQLITE_IOERR

            const myLocks = file.conn.myLocks
            const enterFatalIoMode = () => { hooks.enterFatalIoMode(file.conn) }
            const releaseWalWriteLock = () => { releaseRawWalWriteLock(file.conn) }
            const isLock = (flags & SQLITE_SHM_LOCK) !== 0
            const isUnlock = (flags & SQLITE_SHM_UNLOCK) !== 0
            const isShared = (flags & SQLITE_SHM_SHARED) !== 0
            const isExclusive = (flags & SQLITE_SHM_EXCLUSIVE) !== 0

            // §11.1.2 / §11.1.3: lock slot 0 is WAL_WRITE. The acquire path only
            // mirrors raw WAL_WRITE ownership; commit-specific gate/snapshot
            // initialization happens later, before the first WAL byte is copied
            // into SAB2. Release still goes through the commit-sync helper so ACK,
            // fatal transitions, and WAL_WRITE_HOLDERS stay spec-consistent.
            if (offset === 0 && n === 1 && isExclusive) {
                if (isLock) {
                    if (file.conn.holdsWalWrite && myLocks[0] === 1) {
                        logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_OK, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                        return SQLITE_OK
                    }

                    const rc = acquireWalWriteLock(file.conn, sab4, {
                        tryAcquireWalWriteLock() {
                            if (Atomics.compareExchange(lockView, 0, 0, -1) !== 0) {
                                return false
                            }
                            myLocks[0] = 1
                            return true
                        },
                        enterFatalIoModeForConnection: enterFatalIoMode,
                    })

                    if (rc === SQLITE_OK) {
                        logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_OK, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                        return SQLITE_OK
                    }
                    if (rc === SQLITE_BUSY) {
                        logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_BUSY, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                        return SQLITE_BUSY
                    }
                    logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_IOERR, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                    return SQLITE_IOERR
                }

                if (isUnlock) {
                    if (file.conn.pendingCommitFrame > 0) {
                        const rc = releaseWalWriteAfterCommit(file.conn, sab2, sab3, sab4, {
                            hashPage: hooks.hashPage,
                            postCommitSync(message, transfer) {
                                hooks.postCommitSyncMessage(file.conn, message, transfer)
                            },
                            enterFatalIoModeForConnection: enterFatalIoMode,
                            releaseWalWriteLock,
                        })
                        logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                        return rc
                    }

                    if (file.conn.holdsWalWrite) {
                        releaseWalWriteIfHeld(file.conn, sab4, releaseWalWriteLock)
                    } else if (myLocks[0] === 1) {
                        releaseWalWriteLock()
                    }
                    logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_OK, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                    return SQLITE_OK
                }
            }

            if (isLock && isExclusive) {
                const newlyAcquiredExclusive: number[] = []
                const upgradedSharedToExclusive: number[] = []
                for (let i = offset; i < offset + n; i++) {
                    const localState = myLocks[i]
                    if (localState === 1) {
                        continue
                    }

                    if (localState === 0) {
                        if (Atomics.compareExchange(lockView, i, 1, -1) !== 1) {
                            for (const acquiredIndex of newlyAcquiredExclusive) {
                                Atomics.store(lockView, acquiredIndex, 0)
                                myLocks[acquiredIndex] = -1
                            }
                            for (const upgradedIndex of upgradedSharedToExclusive) {
                                Atomics.store(lockView, upgradedIndex, 1)
                                myLocks[upgradedIndex] = 0
                            }
                            logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_BUSY, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                            return SQLITE_BUSY
                        }
                        myLocks[i] = 1
                        upgradedSharedToExclusive.push(i)
                        continue
                    }

                    if (localState !== -1) {
                        for (const acquiredIndex of newlyAcquiredExclusive) {
                            Atomics.store(lockView, acquiredIndex, 0)
                            myLocks[acquiredIndex] = -1
                        }
                        for (const upgradedIndex of upgradedSharedToExclusive) {
                            Atomics.store(lockView, upgradedIndex, 1)
                            myLocks[upgradedIndex] = 0
                        }
                        logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_BUSY, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                        return SQLITE_BUSY
                    }

                    if (Atomics.compareExchange(lockView, i, 0, -1) !== 0) {
                        // Failed — roll back what we acquired or upgraded
                        for (const acquiredIndex of newlyAcquiredExclusive) {
                            Atomics.store(lockView, acquiredIndex, 0)
                            myLocks[acquiredIndex] = -1
                        }
                        for (const upgradedIndex of upgradedSharedToExclusive) {
                            Atomics.store(lockView, upgradedIndex, 1)
                            myLocks[upgradedIndex] = 0
                        }
                        logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_BUSY, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                        return SQLITE_BUSY
                    }
                    myLocks[i] = 1
                    newlyAcquiredExclusive.push(i)
                }
                logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_OK, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                return SQLITE_OK
            }

            if (isLock && isShared) {
                const acquired: number[] = []
                // §4.3: Shared lock — increment refcount if no exclusive holder
                for (let i = offset; i < offset + n; i++) {
                    const localState = myLocks[i]
                    if (localState === 1) {
                        continue
                    }
                    if (localState === 0) {
                        continue
                    }
                    if (!lockSharedAtomic(lockView, i)) {
                        // Failed — roll back what we acquired
                        for (const acquiredIndex of acquired) {
                            Atomics.sub(lockView, acquiredIndex, 1)
                            myLocks[acquiredIndex] = -1
                        }
                        logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_BUSY, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                        return SQLITE_BUSY
                    }
                    myLocks[i] = 0
                    acquired.push(i)
                }
                logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_OK, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                return SQLITE_OK
            }

            if (isUnlock && isExclusive) {
                // Release exclusive: set to 0 (unlocked)
                for (let i = offset; i < offset + n; i++) {
                    if (myLocks[i] !== 1) {
                        continue
                    }
                    Atomics.store(lockView, i, 0)
                    myLocks[i] = -1
                }
                logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_OK, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                return SQLITE_OK
            }

            if (isUnlock && isShared) {
                // Release shared: decrement refcount
                for (let i = offset; i < offset + n; i++) {
                    if (myLocks[i] !== 0) {
                        continue
                    }
                    Atomics.sub(lockView, i, 1)
                    myLocks[i] = -1
                }
                logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_OK, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
                return SQLITE_OK
            }

            logLockTrace('xShmLock:done', { pFile, offset, n, flags, rc: SQLITE_IOERR, lockView: Array.from(lockView), myLocks: Array.from(myLocks) })
            return SQLITE_IOERR
        },

        /**
         * xShmBarrier — masked push, full pull (§96 §17).
         *
         * Pushes dirty WASM shadow words (only those owned by this connection)
         * to SAB3, then pulls all shared words back.
         */
        xShmBarrier(_pFile: number): void {
            // §17: Masked push, full pull.
            // For each mapped SHM region, push dirty words that this
            // connection owns (per allowedMask), then pull all words back.
            const file = openFiles.get(_pFile)
            if (!file || file.shmRegions.size === 0) return

            // SAB3 SHM data as Int32Array for atomic access
            const sab3I32 = sab3ShmInt32View(sab3)
            // Compute allowed mask from this connection's held SHM locks
            const mask = allowedBarrierMask(file.conn.myLocks, sab3I32.length)

            for (const [regionIndex, region] of file.shmRegions) {
                const wordCount = region.size / Int32Array.BYTES_PER_ELEMENT
                const activeI32 = new Int32Array(wasm.heap8u().buffer, region.wasmPtr, wordCount)
                const cleanI32 = region.cleanI32
                const globalWordOffset = regionIndex * wordCount

                // Push: write dirty words that are in the allowed mask
                for (let i = 0; i < wordCount; i++) {
                    if (activeI32[i] !== cleanI32[i]) {
                        const globalIdx = globalWordOffset + i
                        if (globalIdx < sab3I32.length && mask.has(globalIdx)) {
                            Atomics.store(sab3I32, globalIdx, activeI32[i])
                        }
                    }
                }

                // SQLite's walIndexWriteHdr() writes header copy B, barriers,
                // then writes copy A. With shadow-backed SHM the post-barrier
                // copy-A store is still local shadow state, so publish a
                // coherent shared header pair here for WRITE/RECOVER owners.
                if (regionIndex === 0 && holdsWalIndexHeaderWriteOwnership(file.conn.myLocks)) {
                    mirrorDirtyWalIndexHeaderIntoPrimaryCopy(sab3I32, activeI32, cleanI32)
                }

                // Pull: read all words from SAB3 into active shadow and clean baseline
                for (let i = 0; i < wordCount; i++) {
                    const globalIdx = globalWordOffset + i
                    const v = globalIdx < sab3I32.length ? Atomics.load(sab3I32, globalIdx) : 0
                    activeI32[i] = v
                    cleanI32[i] = v
                }
            }
        },

        /**
         * xShmUnmap — deallocate SHM shadows.
         */
        xShmUnmap(pFile: number, _deleteFlag: number): number {
            const file = openFiles.get(pFile)
            if (file) {
                for (const [_, region] of file.shmRegions) {
                    wasm.dealloc(region.wasmPtr)
                }
                file.shmRegions.clear()
            }
            return SQLITE_OK
        },
    }

    // ── VFS methods ────────────────────────────────────────────

    const sbVfs = new capi.sqlite3_vfs()
    sbVfs.$iVersion = 2
    sbVfs.$szOsFile = capi.sqlite3_file.structInfo.sizeof
    sbVfs.$mxPathname = 512

    // Inherit xRandomness/xSleep from default VFS
    const pDefault = capi.sqlite3_vfs_find(null)
    if (pDefault) {
        const dVfs = new capi.sqlite3_vfs(pDefault)
        sbVfs.$xRandomness = dVfs.$xRandomness
        sbVfs.$xSleep = dVfs.$xSleep
        dVfs.dispose()
    }

    const vfsMethods = {
        /**
         * xOpen — route file type via open flags.
         *
         * §96 §2.4: MainDb → SAB1, WAL → SAB2, Journal → no-op, SHM → SAB3.
         */
        xOpen(
            pVfs: number, zName: number, pFile: number,
            flags: number, pOutFlags: number,
        ): number {
            const heap = wasm.heap8u()
            const name = cStringFromHeap(heap, zName)
            const kind = (flags & SQLITE_OPEN_MAIN_DB) ? VfsFileKind.MainDb
                : (flags & SQLITE_OPEN_WAL) ? VfsFileKind.Wal
                : (flags & SQLITE_OPEN_MAIN_JOURNAL) ? VfsFileKind.Journal
                : classifyVirtualFilename(name)
            const created = (flags & SQLITE_OPEN_CREATE) !== 0

            if (kind === VfsFileKind.MainDb && created) {
                mainDbCreated = true
            } else if (kind === VfsFileKind.Wal && created) {
                walCreated = true
            } else if (kind === VfsFileKind.Journal && created) {
                journalCreated = true
            }

            sdkConsole.trace('[sqlite3mt:vfs] xOpen ' + JSON.stringify({
                name,
                kind,
                flags,
                created,
                mainDbExists: hasMainDb(),
                walExists: hasWal(),
                journalExists: hasJournal(),
            }))

            // Allocate file state on WASM heap
            const statePtr = wasm.alloc(64) // generous size for struct
            const fileState: RegisteredFileState = {
                fileType: kind,
                conn,
                shmRegions: new Map(),
                lockLevel: 0,
                selfPtr: statePtr,
                exists: kind === VfsFileKind.MainDb
                    ? hasMainDb()
                    : kind === VfsFileKind.Wal
                        ? hasWal()
                        : kind === VfsFileKind.Journal
                            ? hasJournal()
                            : false,
            }
            openFiles.set(pFile, fileState)

            const sq3File = new capi.sqlite3_file(pFile)
            sq3File.$pMethods = sbIo.pointer
            sq3File.dispose()

            if (pOutFlags) wasm.poke32(pOutFlags, flags)

            return 0 // SQLITE_OK
        },

        /** xDelete — clear virtual file state when SQLite deletes the file. */
        xDelete(_pVfs: number, zName: number, _doSyncDir: number): number {
            const name = cStringFromHeap(wasm.heap8u(), zName)
            sdkConsole.trace('[sqlite3mt:vfs] xDelete ' + JSON.stringify({ name, kind: classifyVirtualFilename(name) }))
            const kind = classifyVirtualFilename(name)
            if (kind === VfsFileKind.Wal) {
                Atomics.store(walSizeView, 0, 0)
                walCreated = false
            } else if (kind === VfsFileKind.Journal) {
                journalBuffer = new Uint8Array(0)
                journalSize = 0
                journalCreated = false
            }
            return 0
        },

        /** xAccess — report existence truthfully so SQLite can decide create/recovery paths correctly. */
        xAccess(_pVfs: number, zName: number, _flags: number, pOut: number): number {
            const name = cStringFromHeap(wasm.heap8u(), zName)
            const kind = classifyVirtualFilename(name)
            const exists = kind === VfsFileKind.MainDb
                ? hasMainDb()
                : kind === VfsFileKind.Wal
                    ? hasWal()
                    : kind === VfsFileKind.Journal
                        ? hasJournal()
                        : kind === VfsFileKind.Shm
                            ? hasWal()
                            : false
            sdkConsole.trace('[sqlite3mt:vfs] xAccess', {
                name,
                kind,
                exists,
                dbPageCount: Atomics.load(sab1CtrlView(sab1), SAB1_DB_PAGE_COUNT_I32),
                walSize: Atomics.load(walSizeView, 0),
                journalSize,
            })
            wasm.poke32(pOut, exists ? 1 : 0)
            return 0
        },

        xFullPathname(_pVfs: number, zName: number, nOut: number, pOut: number): number {
            sdkConsole.trace('[sqlite3mt:vfs] xFullPathname ' + JSON.stringify({ name: cStringFromHeap(wasm.heap8u(), zName), nOut }))
            if (nOut <= 0) {
                return SQLITE_CANTOPEN
            }

            const heap = wasm.heap8u()
            let copied = 0
            while (copied < nOut - 1) {
                const byte = heap[zName + copied]!
                heap[pOut + copied] = byte
                copied += 1
                if (byte === 0) {
                    return SQLITE_OK
                }
            }

            heap[pOut + nOut - 1] = 0
            return SQLITE_CANTOPEN
        },

        xCurrentTime(_pVfs: number, pOut: number): number {
            sdkConsole.trace('[sqlite3mt:vfs] xCurrentTime')
            wasm.poke(pOut, 2440587.5 + (Date.now() / 86400000), 'double')
            return SQLITE_OK
        },

        xCurrentTimeInt64(_pVfs: number, pOut: number): number {
            wasm.poke(pOut, (2440587.5 * 86400000) + Date.now(), 'i64')
            return SQLITE_OK
        },

        xGetLastError(_pVfs: number, _nOut: number, _pOut: number): number {
            return SQLITE_OK
        },
    }

    // ── Install ────────────────────────────────────────────────

    sqlite3.vfs.installVfs({
        io: { struct: sbIo, methods: ioMethods },
    })

    sqlite3.vfs.installVfs({
        vfs: { struct: sbVfs, methods: vfsMethods, name: vfsName },
    })

    return {
        get vfsName() { return vfsName },
        get ioPointer() { return sbIo.pointer },
        pushMappedShmRegionsToShared() {
            const sab3I32 = sab3ShmInt32View(sab3)

            for (const file of openFiles.values()) {
                if (file.shmRegions.size === 0) {
                    continue
                }

                for (const [regionIndex, region] of file.shmRegions) {
                    const wordCount = region.size / Int32Array.BYTES_PER_ELEMENT
                    const activeI32 = new Int32Array(wasm.heap8u().buffer, region.wasmPtr, wordCount)
                    const globalWordOffset = regionIndex * wordCount

                    for (let i = 0; i < wordCount; i += 1) {
                        const globalIdx = globalWordOffset + i
                        if (globalIdx < sab3I32.length) {
                            Atomics.store(sab3I32, globalIdx, activeI32[i]!)
                        }
                    }
                }
            }
        },
        pullMappedShmRegionsFromShared() {
            const sab3I32 = sab3ShmInt32View(sab3)

            for (const file of openFiles.values()) {
                if (file.shmRegions.size === 0) {
                    continue
                }

                for (const [regionIndex, region] of file.shmRegions) {
                    const wordCount = region.size / Int32Array.BYTES_PER_ELEMENT
                    const activeI32 = new Int32Array(wasm.heap8u().buffer, region.wasmPtr, wordCount)
                    const cleanI32 = region.cleanI32
                    const globalWordOffset = regionIndex * wordCount

                    for (let i = 0; i < wordCount; i += 1) {
                        const globalIdx = globalWordOffset + i
                        const value = globalIdx < sab3I32.length ? Atomics.load(sab3I32, globalIdx) : 0
                        activeI32[i] = value
                        cleanI32[i] = value
                    }
                }
            }
        },
        releaseLeakedShmLocks(options?: ReleaseLeakedShmLocksOptions) {
            const seen = new Set<ClientWorkerConnState>()
            for (const file of openFiles.values()) {
                if (seen.has(file.conn)) {
                    continue
                }
                seen.add(file.conn)
                releaseLeakedShmLocksForConnection(file.conn, options)
            }
        },
        dispose() {
            const seen = new Set<ClientWorkerConnState>()
            for (const file of openFiles.values()) {
                if (seen.has(file.conn)) {
                    continue
                }
                seen.add(file.conn)
                releaseLeakedShmLocksForConnection(file.conn)
            }
            // Clean up all open file states
            for (const [pFile, file] of openFiles) {
                for (const [_, region] of file.shmRegions) {
                    wasm.dealloc(region.wasmPtr)
                }
                if (file.selfPtr) wasm.dealloc(file.selfPtr)
            }
            openFiles.clear()
            sbIo.dispose()
            sbVfs.dispose()
        },
    }
}
